import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { HelpCircle, Lock, FileText, HeartHandshake, Users, BadgeCheck, ShieldCheck, ArrowRight, Mail, Search, X } from "lucide-react";
import { searchInfoTopics, highlightMatch } from "@/lib/infoTopics";


/**
 * Render `text` with case-insensitive highlights around each `query` match.
 * Reads the parts array from `highlightMatch()`; returns a plain string when
 * no query is set (no unnecessary spans).
 */
function renderHighlighted(text, query) {
  const parts = highlightMatch(text, query);
  if (typeof parts === "string") return parts;
  return parts.map((p, i) =>
    p.hit ? (
      <mark key={i} className="bg-amber-200 text-slate-900 rounded px-0.5">
        {p.t}
      </mark>
    ) : (
      <span key={i}>{p.t}</span>
    )
  );
}


const HUB_ITEMS = [
  {
    to: "/info/faqs",
    icon: <HelpCircle className="w-6 h-6" />,
    tone: "pink",
    eyebrow: "Help",
    title: "FAQs",
    blurb: "Quick answers to the questions we hear most from parents and kids.",
    tid: "hub-item-faqs",
  },
  {
    to: "/info/how-it-works",
    icon: <BadgeCheck className="w-6 h-6" />,
    tone: "teal",
    eyebrow: "The basics",
    title: "How It Works",
    blurb: "The three-step flow from listing to sale — with parent approval at every gate.",
    tid: "hub-item-how-it-works",
  },
  {
    to: "/info/about",
    icon: <Users className="w-6 h-6" />,
    tone: "pink",
    eyebrow: "Our story",
    title: "About Us",
    blurb: "A Hackney-born app co-designed with real primary school coding-club kids.",
    tid: "hub-item-about",
  },
  {
    to: "/info/safety",
    icon: <ShieldCheck className="w-6 h-6" />,
    tone: "teal",
    eyebrow: "Trust & safety",
    title: "Parental Safety Guidelines",
    blurb: "Preset messaging, PIN Parent Zone, family-safety review, UK GDPR & Data Protection.",
    tid: "hub-item-safety",
  },
  {
    to: "/info/privacy",
    icon: <Lock className="w-6 h-6" />,
    tone: "slate",
    eyebrow: "Legal",
    title: "Privacy Policy",
    blurb: "What we collect, what we don't, and how to ask us to delete it.",
    tid: "hub-item-privacy",
  },
  {
    to: "/info/terms",
    icon: <FileText className="w-6 h-6" />,
    tone: "slate",
    eyebrow: "Legal",
    title: "Terms & Conditions",
    blurb: "The house rules for using MyTableTopApp — plain English, no gotchas.",
    tid: "hub-item-terms",
  },
];

const TONE_MAP = {
  pink: {
    wrap: "bg-brandPink/15 text-brandPink",
    eyebrow: "text-brandPink",
    hover: "hover:border-brandPink/40",
  },
  teal: {
    wrap: "bg-brandTeal/15 text-brandTeal",
    eyebrow: "text-brandTeal",
    hover: "hover:border-brandTeal/40",
  },
  slate: {
    wrap: "bg-brandSlate/10 text-brandSlate",
    eyebrow: "text-brandSlate",
    hover: "hover:border-brandSlate/30",
  },
};

export default function InfoHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");

  // Keep ?q= in the URL so parents can share a specific search result.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (query.trim().length >= 2) {
      next.set("q", query.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo(() => searchInfoTopics(query), [query]);
  const isSearching = query.trim().length >= 2;

  return (
    <div className="min-h-screen bg-[#FFFDF7]" data-testid="info-hub">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pt-14 lg:pt-20 pb-6">
        <div className="pill bg-brandTeal/15 text-brandTeal" data-testid="hub-eyebrow">
          <HelpCircle className="w-3.5 h-3.5 mr-1.5" /> Information Hub
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-brandSlate leading-[1.05] mt-4" data-testid="hub-title">
          Everything you need to know, in one place.
        </h1>
        <p className="text-lg text-slate-600 mt-4 max-w-2xl leading-relaxed">
          Guides, policies, and safety-first practices for parents and kids using MyTableTopApp.
        </p>

        {/* Search bar — indexes FAQ answers + every section of every policy page. */}
        <div className="mt-8 relative max-w-2xl" data-testid="hub-search-wrap">
          <label htmlFor="hub-search" className="sr-only">Search the Information Hub</label>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              id="hub-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQs, safety guidelines, privacy…"
              className="w-full pl-11 pr-10 py-3 rounded-2xl border border-slate-200 bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-brandTeal focus:border-brandTeal transition"
              data-testid="hub-search-input"
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                aria-label="Clear search"
                data-testid="hub-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {isSearching && (
            <div className="mt-2 text-xs text-slate-500" data-testid="hub-search-count">
              {results.length === 0
                ? "No matching answers — try a different word."
                : `${results.length} matching answer${results.length === 1 ? "" : "s"}`}
            </div>
          )}
        </div>
      </section>

      {isSearching ? (
        /* Search results view */
        <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-12 space-y-4" data-testid="hub-search-results">
          {results.length === 0 && (
            <div className="card-base p-8 text-center" data-testid="hub-search-empty">
              <div className="text-slate-500 text-sm">
                No answers matched <strong>&quot;{query}&quot;</strong>. Try a shorter phrase, or{" "}
                <Link
                  to="/contact?topic=general_feedback"
                  className="text-brandTeal font-semibold underline hover:no-underline"
                  data-testid="hub-search-empty-contact"
                >
                  send us a message
                </Link>{" "}
                and we&apos;ll help you out.
              </div>
            </div>
          )}
          {results.map((r) => (
            <Link
              key={`${r.topicSlug}-${r.sectionIndex}`}
              to={`/info/${r.topicSlug}?q=${encodeURIComponent(query.trim())}#section-${r.sectionIndex}`}
              className="card-base p-5 md:p-6 block hover:-translate-y-0.5 transition-all border border-transparent hover:border-brandTeal/30"
              data-testid={`hub-search-result-${r.topicSlug}-${r.sectionIndex}`}
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-brandTeal">
                <ShieldCheck className="w-3 h-3" /> {r.topicEyebrow} · {r.topicTitle}
              </div>
              <div className="font-display text-lg font-semibold text-brandSlate mt-2">
                {renderHighlighted(r.h, query)}
              </div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed line-clamp-3">
                {renderHighlighted(r.p, query)}
              </p>
              <div className="mt-3 text-xs font-semibold text-brandSlate inline-flex items-center gap-1">
                Read the full section <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </section>
      ) : (
        /* Default topic grid */
        <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-12 grid md:grid-cols-2 gap-5" data-testid="hub-grid">
          {HUB_ITEMS.map((item) => {
            const tone = TONE_MAP[item.tone];
            return (
              <Link
                key={item.tid}
                to={item.to}
                className={`card-base p-6 md:p-7 flex gap-5 hover:-translate-y-0.5 transition-all ${tone.hover}`}
                data-testid={item.tid}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0 ${tone.wrap}`}>
                  {item.icon}
                </div>
                <div className="flex-1">
                  <div className={`text-[11px] font-bold uppercase tracking-[0.15em] ${tone.eyebrow}`}>{item.eyebrow}</div>
                  <div className="font-display text-xl font-semibold text-brandSlate mt-0.5">{item.title}</div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{item.blurb}</p>
                  <div className="mt-3 text-xs font-semibold text-brandSlate inline-flex items-center gap-1">
                    Read more <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </section>
      )}

      {/* Contact strip */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-20">
        <div className="card-base p-6 md:p-8 bg-gradient-to-br from-brandPink/10 via-white to-brandTeal/10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandTeal text-white">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold text-brandSlate">Still stuck?</div>
              <div className="text-sm text-slate-600">We reply within 24 hours — faster for safety concerns.</div>
            </div>
          </div>
          <Link
            to="/contact"
            className="btn-primary"
            data-testid="hub-contact-form-link"
          >
            Contact us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
